import React, { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react'
import CommonLayout from '../../../layouts/Common'
import { ConversationContainer, CreateMessageContainer, GoToClientButton, InmailWrapper, InputMessageSubWrapper, InputMessageWrapper, LeftChat, MessagesList, NewMessageInputContainer, NewMessageWrapper, RightChat, StyledCard, StyledForm, UserContainer } from './style';
import { Avatar, Button, Input, notification, Select, Spin, Upload, UploadFile, UploadProps } from 'antd';
import { CheckCircleOutlined, UserOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import useGetMessages, { useSendMessage } from '../../../hooks/professionals/useGetMessages';
import { formatDate } from '../../../helpers';
import { Message } from '../../../redux/slices/initial_states/type';
import { ClientType } from '../../../types';
import Tiptap from '../../../components/Common/Form/Tiptap';
import { StyledButton } from '../../../components/Common/Button/style';
import MaterialIcon from '../../../components/Icons/MaterialIcon';
import { _FORM_ITEM_LAYOUT } from '../../../constants';
import { NotificationTitle } from '../../../components/Common/Notification/style';
import { theme } from '../../../styles/theme';
import { useNavigate, useParams } from 'react-router-dom';

interface UserListProps {
  client: ClientType
  clientSelected: boolean
  setInmailLength: (inmailLength: number) => void
  setSelectedMessages: Dispatch<SetStateAction<Message[] | undefined>>
  onSelectClient: (clientId: string) => void
}

interface InputMessageProps {
  content: string
  onChange: (content: string) => void
}

const UserList: React.FC<UserListProps> = ({
  client,
  clientSelected,
  setInmailLength,
  setSelectedMessages,
  onSelectClient
}) => {
  const { data: messages, loading } = useGetMessages(client.id);

  const onMessageClick = () => {
    onSelectClient(client.id);
    setSelectedMessages(messages);
  };

  const sortedMessages = useMemo(() => (
    messages && [...messages].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
  ), [messages]);

  useEffect(() => {
    setInmailLength(messages?.length);
  }, [messages, setInmailLength]);

  if (loading) {
    return <Spin size="large" />
  }

  if (messages?.length <= 0 || messages === undefined) {
    setInmailLength(0);
    return null;
  }

  return (
    <UserContainer
      selected={clientSelected}
      onClick={onMessageClick}
      key={`user_${client.id}`}
    >
      <Button
        className="pin"
        type="text"
        icon={
          <i className="material-icons-sharp" style={{ fontSize: 'inherit', transform: 'rotate(45deg)' }}>push_pin</i>
        }
      />
      <Avatar size={64} icon={<UserOutlined />} />
      <div className="userinfo">
        <p className="name">{client.firstName} {client.familyName}</p>
        <div className="message" dangerouslySetInnerHTML={{ __html: sortedMessages[0]?.content }} />
        <p className="date">{formatDate(sortedMessages[0]?.createdAt)}</p>
      </div>
    </UserContainer>
  );
};

const InputMessage: React.FC<InputMessageProps> = ({ content, onChange }) => {
  const [form] = StyledForm.useForm();
  return (
    <InputMessageSubWrapper>
      <StyledForm.Item
        labelAlign="left"
        name="title"
        label="Title"
      >
        <Input />
      </StyledForm.Item>
      <Tiptap
        content={content}
        onChange={onChange}
      />
    </InputMessageSubWrapper>
  )
};

const NewInputMessage: React.FC<InputMessageProps> = ({ content, onChange }) => {
  const [api, contextHolder] = notification.useNotification();
  const [form] = StyledForm.useForm();
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const clients = useSelector((state: any) => state.professionals.listClients);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const { sendMessage, loading: loadingSendMessage } = useSendMessage();
  const { refetch: refetchMessages } = useGetMessages(selectedClientId);

  const clientsOptions = useMemo(() => {
    return clients?.map((client: ClientType) => ({
      label: `${client.firstName} ${client.familyName}`,
      value: client.id
    }))
  }, [clients]);

  const onSelectClient = (value: any) => {
    setSelectedClientId(value)
  };

  const props: UploadProps = {
    onRemove: (file) => {
      const index = fileList.indexOf(file);
      const newFileList = fileList.slice();
      newFileList.splice(index, 1);
      setFileList(newFileList);
    },
    beforeUpload: (file) => {
      setFileList([...fileList, file]);

      return false;
    },
    fileList,
  };

  const onFinish = async (values: any) => {
    const data = {
      ...values,
      attachment: fileList
    };

    try {
      const res = await sendMessage(data);
      console.log(res);
      if (res?.professionalSendMessage?.success) {
        api.info({
          message: <NotificationTitle>{res.professionalSendMessage?.message}</NotificationTitle>,
          placement: 'topRight',
          icon:
            <CheckCircleOutlined
              style={{
                color: theme.color.primary,
              }}
            />,
          style: {
            background: `${theme.bg.secondary}`,
            borderRadius: 10,
            alignItems: 'center',
            display: 'flex'
          }
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      refetchMessages();
    }
  };

  return (
    <StyledForm
      {..._FORM_ITEM_LAYOUT}
      form={form}
      name="inmail_form"
      onFinish={onFinish}
      initialValues={{}}
      layout="vertical"
      scrollToFirstError
    >
      {contextHolder}
      <NewMessageInputContainer>
        <div>
          <StyledForm.Item
            labelAlign="left"
            name="clientId"
            label="To:"
          >
            <Select
              showSearch
              placeholder="Name of recipient"
              optionFilterProp="label"
              onChange={onSelectClient}
              // onSearch={onSearch}
              options={clientsOptions}
            />
          </StyledForm.Item>
          <StyledForm.Item
            labelAlign="left"
            name="title"
            label="Title"
          >
            <Input placeholder="Title (optional)" />
          </StyledForm.Item>
          <StyledForm.Item
            labelAlign="left"
            name="content"
            label=""
          >
            <Tiptap
              content={content}
              onChange={onChange}
            />
          </StyledForm.Item>
        </div>
        <div className="send-footer">
          <div>
            <StyledButton loading={loadingSendMessage} htmlType="submit" type="primary" padding="0.75rem 1rem">
              Send
            </StyledButton>
            <Upload {...props}>
              <Button
                type="text"
                icon={
                  <MaterialIcon
                    icon="attachment"
                    className="material-icons-sharp"
                    style={{ fontSize: 24, color: '#718096' }}
                  />
                }
              />
            </Upload>
          </div>
        </div>
      </NewMessageInputContainer>
    </StyledForm>
  )
}

const InMail = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const clients = useSelector((state: any) => state.professionals.listClients);
  const [replying, setReplying] = useState<boolean>(false);
  const [selectedClientId, setSelectedClientId] = useState<any>();
  const [selectedMessages, setSelectedMessages] = useState<Message[]>();
  const [inmailLength, setInmailLength] = useState<number>(0);
  const [content, setContent] = useState<string>('');
  const [newMessage, setNewMessage] = useState<boolean>(false);

  const onChangeMessage = (value: string) => {
    setContent(value);
  };

  const clientName = useMemo(() => {
    const filteredClient = clients?.filter((client: any) => client.id === selectedClientId)[0];
    return `${filteredClient?.firstName} ${filteredClient?.familyName}`;
  }, [selectedClientId, clients]);

  const onSelectClient = (id: string) => {
    setNewMessage(false);
    setSelectedClientId(id);
  };


  const onVisitClient = () => {
    navigate(`/p/${code}/clients/${selectedClientId}`)
  };

  const onCreateNewMessage = () => {
    setSelectedClientId(null);
    setReplying(false);
    setSelectedMessages([]);
    setNewMessage(true);
  };

  return (
    <CommonLayout
      title="Inmail"
      width="95%"
      customButton={
        selectedClientId &&
        <GoToClientButton
          onClick={onVisitClient}
          icon={
            <i className="material-icons-sharp" style={{ fontSize: 'inherit' }}>arrow_forward</i>
          }
          iconPosition="end"
        >
          {clientName}
        </GoToClientButton>
      }
    >
      <StyledCard>
        <MessagesList>
          <CreateMessageContainer>
            <Button
              onClick={onCreateNewMessage}
              type="text"
              icon={
                <i className="material-icons-sharp" style={{ fontSize: 'inherit' }}>create</i>
              }
            />
          </CreateMessageContainer>
          {inmailLength <= 0 && (
            <NewMessageWrapper selected={newMessage}>
              <div>
                <Avatar size={46} icon={<UserOutlined />} />
                <p>New Message</p>
              </div>
            </NewMessageWrapper>
          )}
          {clients?.map((client: any) => (
            <UserList
              key={client.id}
              client={client}
              setSelectedMessages={setSelectedMessages}
              onSelectClient={onSelectClient}
              setInmailLength={setInmailLength}
              clientSelected={client.id === selectedClientId}
            />
          ))}
        </MessagesList>
        <ConversationContainer>
          {selectedClientId && (
            <div className="header">
              <Avatar size={55} icon={<UserOutlined />} />
              <h1 className="clientname">{clientName}</h1>
            </div>
          )}
          {Boolean(selectedMessages?.length) && (
            <>
              <InmailWrapper>
                {selectedMessages?.map((message: Message) => {
                  if (message?.isFromClient) {
                    return (
                      <LeftChat key={message?.id}>
                        <Avatar size={48} icon={<UserOutlined />} />
                        <div className="message">
                          <div className="content">
                            <p className="title">{message?.title}</p>
                            <div dangerouslySetInnerHTML={{ __html: message?.content }} />
                          </div>
                          <div className="datetime">
                            <small>{formatDate(message.createdAt as string)}</small>
                          </div>
                        </div>
                      </LeftChat>
                    )
                  }
                  return (
                    <RightChat key={message?.id}>
                      <Avatar size={48} icon={<UserOutlined />} />
                      <div className="message">
                        <div className="content">
                          <p className="title">{message?.title}</p>
                          <div dangerouslySetInnerHTML={{ __html: message?.content }} />
                        </div>
                        <div className="datetime">
                          <small>{formatDate(message.createdAt as string)}</small>
                        </div>
                      </div>
                    </RightChat>
                  )
                })}
              </InmailWrapper>

              <InputMessageWrapper>
                {!replying && (
                  <Button
                    onClick={() => setReplying(true)}
                    type="primary"
                    icon={
                      <i className="material-icons-sharp" style={{ fontSize: 'inherit' }}>reply</i>
                    }
                    iconPosition="start"
                  >
                    Reply
                  </Button>
                )}
                {replying && (
                  <InputMessage
                    content={content}
                    onChange={onChangeMessage}
                  />
                )}
              </InputMessageWrapper>
            </>
          )}
          {newMessage && (
            <NewInputMessage
              content={content}
              onChange={onChangeMessage}
            />
          )}
        </ConversationContainer>
      </StyledCard>
    </CommonLayout>
  )
}

export default InMail